import React, { useState, useEffect } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  addDoc,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import Footer from "./Footer";
import Navbar from "./Navbar";

function BookSession() {
  const [user, setUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [formData, setFormData] = useState({
    date: "",
    time: "",
    type: "1:1 Coaching",
    notes: "",
  });
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        fetchSessions(user.uid);
      } else {
        setUser(null);
      }
    });
    return () => unsub();
  }, []);

  const fetchSessions = async (uid) => {
    const q = query(collection(db, "sessions"), where("userId", "==", uid));
    const snapshot = await getDocs(q);
    const today = new Date().toISOString().split("T")[0];
    const upcoming = snapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .filter((session) => session.date >= today)
      .sort((a, b) => a.date.localeCompare(b.date));
    setSessions(upcoming);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingSessionId) {
        const sessionRef = doc(db, "sessions", editingSessionId);
        await deleteDoc(sessionRef); // delete and re-add for simplicity
      }

      await addDoc(collection(db, "sessions"), {
        ...formData,
        userId: user.uid,
        email: user.email,
        bookedAt: Timestamp.now(),
      });

      setSubmitted(true);
      setFormData({
        date: "",
        time: "",
        type: "1:1 Coaching",
        notes: "",
      });
      setEditingSessionId(null);
      fetchSessions(user.uid);
    } catch (err) {
      console.error("Booking error:", err);
    }
  };

  const handleReschedule = (session) => {
    setFormData({
      date: session.date,
      time: session.time,
      type: session.type,
      notes: session.notes || "",
    });
    setEditingSessionId(session.id);
    setSubmitted(false);
  };

  const handleCancel = async (sessionId) => {
    if (window.confirm("Are you sure you want to cancel this session?")) {
      await deleteDoc(doc(db, "sessions", sessionId));
      fetchSessions(user.uid);
    }
  };

  if (!user) {
    return (
      <div>
        <Navbar />
        <div className="p-10 text-center bg-white text-gray-800 rounded-xl shadow">
          <h2 className="text-xl font-semibold mb-4">
            Please log in to book a session
          </h2>
          <a
            href="/login"
            className="text-white bg-green-600 px-5 py-2 rounded-md hover:bg-green-700"
          >
            Login
          </a>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="max-w-xl mx-auto p-8 bg-white rounded-lg shadow-md mt-12">
        <h2 className="text-2xl font-bold mb-6 text-center">
          {editingSessionId ? "Reschedule Session" : "Book Your Coaching Session"}
        </h2>

        {submitted && (
          <p className="bg-green-100 text-green-800 p-4 rounded text-center font-semibold mb-4">
            ✅ Your session has been booked. You’ll get a confirmation email shortly.
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium">Date</label>
            <input
              type="date"
              name="date"
              required
              value={formData.date}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-4 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium">Time</label>
            <input
              type="time"
              name="time"
              required
              value={formData.time}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-4 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium">Session Type</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-4 py-2"
            >
              <option>1:1 Coaching</option>
              <option>Business Strategy Call</option>
              <option>Website Review Session</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium">Notes (Optional)</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-4 py-2"
              placeholder="Add any context for your coach..."
            />
          </div>

          <button
            type="submit"
            className="w-full bg-green-600 text-white px-5 py-2 rounded-md hover:bg-green-700 font-semibold"
          >
            {editingSessionId ? "Reschedule Session" : "Book Session"}
          </button>
        </form>
      </div>

      {/* List of upcoming sessions */}
      <div className="max-w-xl mx-auto p-6 mt-6 bg-gray-50 rounded shadow">
        <h3 className="text-lg font-semibold mb-4">📅 Your Upcoming Sessions</h3>
        {sessions.length === 0 ? (
          <p className="text-gray-600">You have no upcoming sessions.</p>
        ) : (
          <ul className="space-y-4">
            {sessions.map((session) => (
              <li
                key={session.id}
                className="p-4 bg-white border border-gray-200 rounded-md shadow-sm"
              >
                <p>
                  <strong>{session.type}</strong> on{" "}
                  <span className="text-gray-800">
                    {session.date} at {session.time}
                  </span>
                </p>
                {session.notes && (
                  <p className="text-sm text-gray-500 mt-1">📝 {session.notes}</p>
                )}
                <div className="mt-2 space-x-3">
                  <button
                    onClick={() => handleReschedule(session)}
                    className="text-blue-600 hover:underline text-sm"
                  >
                    Reschedule
                  </button>
                  <button
                    onClick={() => handleCancel(session.id)}
                    className="text-red-600 hover:underline text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default BookSession;
